import { RuntimeSlideModel } from '../slides/models';
import { SlideScheduleService } from '../slides/slide-schedule.service';

/** Slide order only — no timers, no signals. Refreshed content is staged and swapped in on wrap, so the loop never jumps. */
export class Playlist {
  private slides: readonly RuntimeSlideModel[] = [];
  private pending?: readonly RuntimeSlideModel[];
  private index = -1;

  public constructor(private readonly schedule: SlideScheduleService) { }

  public get current(): RuntimeSlideModel | null {
    return this.slides[this.index] || null;
  }

  public get position(): number {
    return this.index;
  }

  public get count(): number {
    return this.slides.length;
  }

  public start(slides: readonly RuntimeSlideModel[], now: number): RuntimeSlideModel | null {
    this.slides = this.unique(slides);
    this.pending = undefined;
    this.index = -1;

    return this.advance(1, now);
  }

  public stage(slides: readonly RuntimeSlideModel[]): void {
    this.pending = this.unique(slides);
  }

  public advance(direction: 1 | -1, now: number): RuntimeSlideModel | null {
    // Every slide can be ineligible, so the search is bounded to one old loop plus one replacement loop.
    let remaining = this.slides.length + (this.pending?.length || 0) + 1;

    while (remaining-- > 0) {
      let next = this.index + direction;
      const wrapped = next < 0 || next >= this.slides.length;

      if (wrapped) {
        // The loop boundary is the only safe moment to swap: mid-loop the viewer would see the order jump.
        if (this.pending !== undefined) {
          this.slides = this.pending;
          this.pending = undefined;
        }

        next = direction === 1 ? 0 : this.slides.length - 1;
      }

      this.index = next;
      const slide = this.current;

      if (slide && this.schedule.isEligible(slide, now)) {
        return slide;
      }

      if (!this.slides.length) {
        break;
      }
    }

    // Nothing eligible anywhere: the display shows its empty state rather than freezing on a stale slide.
    this.index = -1;

    return null;
  }

  public clear(): void {
    this.slides = [];
    this.pending = undefined;
    this.index = -1;
  }

  // Two feeds can produce the same slide; showing it twice a loop reads as a bug on the wall.
  private unique(slides: readonly RuntimeSlideModel[]): readonly RuntimeSlideModel[] {
    const ids = new Set<string>();

    return slides.filter(slide => {
      if (!slide.id || ids.has(slide.id)) {
        return false;
      }

      ids.add(slide.id);

      return true;
    });
  }
}
